import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { supabase } from '../../core/supabase/supabase.client';

export type RefundRequestStatus =
  | 'pending'
  | 'approved'
  | 'processing'
  | 'failed'
  | 'rejected'
  | 'refunded'
  | 'ALL';

export type RefundRequestedVia = 'phone' | 'email' | 'whatsapp' | 'admin' | 'app';

export interface AdminRefundRequest {
  id: number;
  order_id: number;
  order_number?: string | null;
  customer_id: string;
  payment_id?: number | null;
  amount: number;
  approved_amount?: number;
  processed_amount?: number;
  paid_amount?: number;
  payment_method?: string;
  provider?: string;
  provider_status?: string;
  provider_refund_id?: string;
  failure_message?: string;
  last_attempt_at?: string;
  manual_external_refund?: boolean;
  reason: string;
  requested_via: RefundRequestedVia;
  status: RefundRequestStatus;
  approved_by?: string | null;
  approved_at?: string | null;
  refunded_at?: string | null;
  transaction_reference?: string | null;
  remarks?: string | null;
  created_at: string;
  updated_at?: string | null;
  order_status?: string;
  order_payment_status?: string;
  customer?: {
    name?: string;
    phone?: string;
  };
  store?: {
    store_id?: number;
    name?: string;
  };
}

export interface RefundListFilters {
  status?: RefundRequestStatus;
  customerId?: string;
  storeId?: number;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface RefundListResponse {
  data: AdminRefundRequest[];
  count: number;
  totals: {
    requested: number;
    pending: number;
    approved: number;
    refunded: number;
    rejected: number;
  };
}

export interface CreateRefundRequestInput {
  orderRef: string;
  reason: string;
  requestedVia: RefundRequestedVia;
  amount?: number;
  remarks?: string;
}

@Injectable({ providedIn: 'root' })
export class RefundRequestsService {
  list(filters: RefundListFilters = {}): Observable<RefundListResponse> {
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 20;
    const offset = (page - 1) * pageSize;

    return from(
      supabase
        .rpc('admin_list_refunds_v2', {
          p_status:
            filters.status && filters.status !== 'ALL' ? filters.status : null,
          p_store_id: filters.storeId ?? null,
          p_from: filters.fromDate ?? null,
          p_to: filters.toDate ?? null,
          p_search: filters.search?.trim() || null,
          p_limit: pageSize,
          p_offset: offset,
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const payload = (data ?? {}) as {
            data?: AdminRefundRequest[];
            count?: number;
            totals?: RefundListResponse['totals'];
          };

          return {
            data: payload.data ?? [],
            count: payload.count ?? 0,
            totals: payload.totals ?? {
              requested: 0,
              pending: 0,
              approved: 0,
              refunded: 0,
              rejected: 0,
            },
          };
        })
    );
  }

  create(input: CreateRefundRequestInput): Observable<number> {
    return from(
      supabase
        .rpc('admin_create_refund_request', {
          p_order_ref: input.orderRef.trim(),
          p_reason: input.reason,
          p_requested_via: input.requestedVia,
          p_amount: input.amount ?? null,
          p_remarks: input.remarks ?? null,
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const payload = (data ?? {}) as { request_id?: number };
          if (!payload.request_id) {
            throw new Error('Failed to create refund request');
          }
          return payload.request_id;
        })
    );
  }

  approve(requestId: number): Observable<void> {
    return from(
      supabase
        .rpc('admin_approve_refund_request', { p_request_id: requestId })
        .then(({ error }) => {
          if (error) throw error;
        })
    );
  }

  reject(requestId: number, remarks: string): Observable<void> {
    return from(
      supabase
        .rpc('admin_reject_refund_request', {
          p_request_id: requestId,
          p_remarks: remarks,
        })
        .then(({ error }) => {
          if (error) throw error;
        })
    );
  }

  markRefunded(
    requestId: number,
    transactionReference: string,
    remarks: string,
    amount: number
  ): Observable<void> {
    return from(
      supabase
        .rpc('admin_record_external_refund', {
          p_request_id: requestId,
          p_reference: transactionReference,
          p_reason: remarks,
          p_amount: amount,
        })
        .then(({ error }) => {
          if (error) throw error;
        })
    );
  }

  process(requestId: number): Observable<{ status: string; message?: string }> {
    return from(supabase.functions.invoke('process-refund', { body: { refund_id: requestId } }).then(async ({ data, error }) => {
      if (error) {
        const response = (error as { context?: Response }).context;
        const detail = await response?.json().catch(() => null);
        throw new Error(detail?.error || error.message);
      }
      if (data?.error) throw new Error(data.error);
      return data;
    }));
  }

  history(requestId: number): Observable<unknown> {
    return from(supabase.rpc('admin_refund_history', { p_request_id: requestId }).then(({ data, error }) => {
      if (error) throw error;
      return data;
    }));
  }
}
